import { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Smile, MessageSquare, Edit2, Trash2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { cn } from '../../lib/utils';

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👀', '✅', '❌'];

const formatTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long', 
    day: 'numeric' 
  });
};

export const Message = ({
  message,
  currentUserId,
  onReact,
  onEdit,
  onDelete,
  onOpenThread,
  isThreadReply = false,
  showDateDivider = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const editInputRef = useRef(null);

  const isOwnMessage = message.user_id === currentUserId;

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.setSelectionRange(editContent.length, editContent.length);
    }
  }, [isEditing]);

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== message.content) {
      onEdit(message.id, editContent);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <>
      {showDateDivider && (
        <div className="flex items-center my-4">
          <div className="flex-1 border-t border-gray-700" />
          <span className="px-4 text-xs font-medium text-gray-400 bg-[#1a1d21]">
            {formatDate(message.created_at)}
          </span>
          <div className="flex-1 border-t border-gray-700" />
        </div>
      )}
      
      <div
        className={cn(
          "group relative px-5 py-1.5 hover:bg-[#222529]",
          isThreadReply && "pl-12"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`message-${message.id}`}
      >
        <div className="flex gap-3">
          {/* Avatar */}
          <div 
            className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center text-white text-sm font-medium"
            style={{ backgroundColor: message.user_avatar_color || '#6366F1' }}
          >
            {message.user_name?.charAt(0).toUpperCase() || 'U'}
          </div>

          {/* Message Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-bold text-white text-sm">{message.user_name}</span>
              <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
              {message.is_edited && (
                <span className="text-xs text-gray-500">(edited)</span>
              )}
            </div>

            {isEditing ? (
              <div className="mt-1">
                <textarea
                  ref={editInputRef}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 bg-[#222529] border border-[#1164A3] rounded text-white text-sm resize-none focus:outline-none"
                  rows={2}
                />
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={handleSaveEdit} className="bg-[#007a5a] hover:bg-[#006649]">
                    <Check size={14} className="mr-1" /> Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="text-gray-400">
                    <X size={14} className="mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-gray-200 text-sm whitespace-pre-wrap break-words">
                {message.content}
              </p>
            )}

            {/* Reactions */}
            {message.reactions?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {message.reactions.map((reaction, idx) => (
                  <button
                    key={idx}
                    onClick={() => onReact(message.id, reaction.emoji)}
                    className={cn(
                      "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors",
                      reaction.user_ids.includes(currentUserId)
                        ? "bg-[#1164A3]/20 border-[#1164A3] text-white"
                        : "bg-[#222529] border-gray-700 text-gray-400 hover:border-gray-600"
                    )}
                  >
                    <span>{reaction.emoji}</span>
                    <span>{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Thread Preview (for parent messages) */}
            {!isThreadReply && message.reply_count > 0 && (
              <button
                onClick={() => onOpenThread(message)}
                className="flex items-center gap-2 mt-2 text-[#1d9bd1] text-sm hover:underline"
                data-testid={`thread-${message.id}`}
              >
                {message.thread_participants?.slice(0, 3).map((p, idx) => (
                  <div
                    key={p.id}
                    className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-medium -ml-1 first:ml-0"
                    style={{ backgroundColor: p.avatar_color || '#6366F1' }}
                  >
                    {p.name?.charAt(0).toUpperCase()}
                  </div>
                ))}
                <span className="font-medium">
                  {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                </span>
                {message.thread_preview?.[0] && (
                  <span className="text-gray-500 hidden sm:inline">
                    Last reply {formatTime(message.thread_preview[message.thread_preview.length - 1]?.created_at)}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Hover Actions */}
        {isHovered && !isEditing && (
          <div className="absolute right-4 -top-4 flex items-center bg-[#222529] border border-gray-700 rounded-lg shadow-lg">
            {/* Emoji Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                  <Smile size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2 bg-[#1a1d21] border-gray-700" align="start">
                <div className="flex gap-1">
                  {EMOJI_LIST.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => onReact(message.id, emoji)}
                      className="p-1.5 hover:bg-[#27292d] rounded text-lg"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Reply in Thread */}
            {!isThreadReply && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-gray-400 hover:text-white"
                onClick={() => onOpenThread(message)}
              >
                <MessageSquare size={16} />
              </Button>
            )}

            {/* More Options */}
            {isOwnMessage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#1a1d21] border-gray-700" align="end">
                  <DropdownMenuItem 
                    onClick={() => setIsEditing(true)}
                    className="text-gray-300 focus:bg-[#27292d] focus:text-white"
                  >
                    <Edit2 size={14} className="mr-2" />
                    Edit message
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onDelete(message.id)}
                    className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
                  >
                    <Trash2 size={14} className="mr-2" />
                    Delete message
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>
    </>
  );
};
